export type LineupSlot = {
  /**
   * Lineup row UUID (post-migration 20260504240200). When present on
   * both this slot and a bound assignment, the FK match wins —
   * rename of artist or stage doesn't fragment the binding.
   */
  id?: string | null;
  artist_name: string;
  stage_name: string;
  event_date: string;
  set_start: string;
  set_end: string;
};

export type AssignmentTimeRow = {
  id: string;
  /**
   * crew_assignments.lineup_id UUID FK → lineup.id. When set, the
   * sync_artist_name_on_lineup_change trigger keeps artist_name in
   * sync; this helper short-circuits to "follow the slot's set
   * times" for FK-bound assignments.
   */
  lineup_id?: string | null;
  artist_name: string | null;
  stage_name: string;
  event_date: string;
  filming_start: string;
  filming_end: string;
};

export type AssignmentTimeUpdate = {
  id: string;
  // Emitted only when the slot at this assignment's previous time
  // position now holds a different artist (rename or LineupImportTab
  // swap). Caller writes this through to crew_assignments.
  artist_name?: string;
  filming_start: string;
  filming_end: string;
};

export type AssignmentDeactivation = {
  id: string;
  deactivate: true;
};

export type AssignmentSyncOutcome = AssignmentTimeUpdate | AssignmentDeactivation;

const normalizeTime = (t: string | null | undefined): string => (t ? t.substring(0, 5) : "");

const artistKey = (s: { artist_name: string | null; stage_name: string; event_date: string }) =>
  `${s.artist_name ?? ""}::${s.stage_name}::${s.event_date}`;

const positionKey = (s: {
  stage_name: string;
  event_date: string;
  set_start: string;
  set_end: string;
}) =>
  `${s.stage_name}::${s.event_date}::${normalizeTime(s.set_start)}::${normalizeTime(s.set_end)}`;

// crew_assignments mirrors lineup slots: (artist_name, stage_name,
// event_date, filming_start, filming_end) is a denormalized snapshot
// of the slot, with no FK to lineup. When a producer edits a slot,
// the assignment must follow it — otherwise the schedule shows stale
// data (wrong artist, or worse: two crew members suddenly appearing
// to overlap because their slots' times got swapped under them).
//
// Producer edits this helper rebinds (priority order, top first):
//
// 1. **Slot relabel at the same position** (the LineupImportTab swap
//    UI: artist_name swaps, set_start/set_end stay). Detected via
//    `previousLineup`: find the prev slot for this assignment's
//    artist, then look up the new slot at THAT (stage, day,
//    set_start, set_end) position. If a slot is there with a
//    different artist, RELABEL the assignment to the new artist —
//    keep filming time at the position. This priority comes first
//    because the LineupImportTab's "Move up/down" button literally
//    only swaps names; following the artist by name in this case
//    creates the field-reported "crew overlap each other" symptom.
//
// 2. **Time move** (artist's slot was moved to new set_start/set_end,
//    name unchanged). Lookup by (artist_name, stage, day) in the
//    new lineup; sync filming_start/end to the slot's new times.
//
// 3. **Deactivate** an over-extended resize. If the relabel branch
//    matches but the assignment's filming range extends BEYOND the
//    original slot's bounds (producer extended into adjacent time
//    deliberately), the new artist's slot may be shorter — there's
//    no clean way to carry the over-extension. Emit a deactivate
//    so the producer re-decides explicitly.
//
// The resize-divergence guard is preserved: if the assignment was
// already diverged from the previous slot (e.g. filming a partial
// resize within the slot's bounds), the rebind keeps the diverged
// filming window rather than snapping back to the slot's full
// extent.
export function computeAssignmentTimeUpdates(
  lineup: LineupSlot[],
  assignments: AssignmentTimeRow[],
  previousLineup?: LineupSlot[],
): AssignmentSyncOutcome[] {
  const newByArtist = new Map<string, LineupSlot>();
  const newByPosition = new Map<string, LineupSlot>();
  for (const l of lineup) {
    if (!l.artist_name) continue;
    newByArtist.set(artistKey(l), l);
    newByPosition.set(positionKey(l), l);
  }

  const prevByArtist = previousLineup
    ? (() => {
        const m = new Map<string, LineupSlot>();
        for (const l of previousLineup) {
          if (!l.artist_name) continue;
          m.set(artistKey(l), l);
        }
        return m;
      })()
    : null;

  const outcomes: AssignmentSyncOutcome[] = [];

  // Build a lineup_id lookup once so the FK fast-path is O(1).
  const slotById = new Map<string, LineupSlot>();
  for (const l of lineup) {
    if (l.id) slotById.set(l.id, l);
  }

  for (const a of assignments) {
    if (!a.artist_name) continue;

    // FK FAST PATH: assignment.lineup_id ↔ lineup.id. The
    // sync_artist_name_on_lineup_change trigger keeps artist_name
    // coherent at the DB layer, so the only thing this branch needs
    // to do is sync filming_start/end to the slot's current times
    // when they've moved. If the slot was deleted, mark for
    // deactivation (same contract as the position-heuristic branch
    // below).
    if (a.lineup_id) {
      const fkSlot = slotById.get(a.lineup_id);
      if (!fkSlot) {
        outcomes.push({ id: a.id, deactivate: true });
        continue;
      }
      if (
        normalizeTime(fkSlot.set_start) === normalizeTime(a.filming_start) &&
        normalizeTime(fkSlot.set_end) === normalizeTime(a.filming_end)
      ) {
        continue;
      }
      // Same divergence guard as the legacy branch: a producer
      // resize that shrunk the assignment within the slot's bounds
      // shouldn't snap back to the slot's full extent.
      const slotStartMin = toMinutes(normalizeTime(fkSlot.set_start));
      let slotEndMin = toMinutes(normalizeTime(fkSlot.set_end));
      if (slotEndMin <= slotStartMin) slotEndMin += 24 * 60;
      const aStartMin = toMinutes(normalizeTime(a.filming_start));
      let aEndMin = toMinutes(normalizeTime(a.filming_end));
      if (aEndMin <= aStartMin) aEndMin += 24 * 60;
      const wasShrunkInside = aStartMin >= slotStartMin && aEndMin <= slotEndMin;
      if (wasShrunkInside && (aStartMin !== slotStartMin || aEndMin !== slotEndMin)) {
        // Producer deliberately resized within the slot — don't
        // touch filming times even if the slot moved.
        continue;
      }
      outcomes.push({
        id: a.id,
        filming_start: fkSlot.set_start,
        filming_end: fkSlot.set_end,
      });
      continue;
    }

    // BRANCH 1: previous-position relabel. Wins over BRANCH 2 because
    // the LineupImportTab swap is implemented as a name-swap that
    // keeps positions; following the artist by name there moves
    // assignments onto each other.
    if (prevByArtist) {
      const prev = prevByArtist.get(artistKey(a));
      if (prev) {
        const slotAtPrevPos = newByPosition.get(positionKey(prev));
        if (slotAtPrevPos && slotAtPrevPos.artist_name !== a.artist_name) {
          // Relabel. Decide filming:
          const wasInSync =
            normalizeTime(prev.set_start) === normalizeTime(a.filming_start) &&
            normalizeTime(prev.set_end) === normalizeTime(a.filming_end);
          if (wasInSync) {
            outcomes.push({
              id: a.id,
              artist_name: slotAtPrevPos.artist_name,
              filming_start: slotAtPrevPos.set_start,
              filming_end: slotAtPrevPos.set_end,
            });
            continue;
          }
          // Diverged from prev slot — was the assignment shrunk
          // (within slot) or extended (beyond slot)? Compare in
          // minutes-of-day, allowing a midnight roll on the slot end.
          const prevStartMin = toMinutes(normalizeTime(prev.set_start));
          let prevEndMin = toMinutes(normalizeTime(prev.set_end));
          if (prevEndMin <= prevStartMin) prevEndMin += 24 * 60;
          const aStartMin = toMinutes(normalizeTime(a.filming_start));
          let aEndMin = toMinutes(normalizeTime(a.filming_end));
          if (aEndMin <= aStartMin) aEndMin += 24 * 60;
          const overExtended = aStartMin < prevStartMin || aEndMin > prevEndMin;
          if (overExtended) {
            outcomes.push({ id: a.id, deactivate: true });
            continue;
          }
          // Shrunk within bounds — preserve the resize, just relabel.
          outcomes.push({
            id: a.id,
            artist_name: slotAtPrevPos.artist_name,
            filming_start: a.filming_start,
            filming_end: a.filming_end,
          });
          continue;
        }
      }
    }

    // BRANCH 2: artist still in the lineup at a new time position.
    const sameArtistSlot = newByArtist.get(artistKey(a));
    if (sameArtistSlot) {
      if (
        normalizeTime(sameArtistSlot.set_start) === normalizeTime(a.filming_start) &&
        normalizeTime(sameArtistSlot.set_end) === normalizeTime(a.filming_end)
      ) {
        continue;
      }
      if (prevByArtist) {
        const prev = prevByArtist.get(artistKey(a));
        if (
          prev &&
          (normalizeTime(prev.set_start) !== normalizeTime(a.filming_start) ||
            normalizeTime(prev.set_end) !== normalizeTime(a.filming_end))
        ) {
          continue;
        }
      }
      outcomes.push({
        id: a.id,
        filming_start: sameArtistSlot.set_start,
        filming_end: sameArtistSlot.set_end,
      });
      continue;
    }

    // BRANCH 3: orphan-style relabel (artist gone, slot at prev pos
    // taken by another artist). Same machinery as branch 1 but
    // reached when prev wasn't enough to trigger relabel above —
    // kept for symmetry with the previous helper version.
    if (!prevByArtist) continue;
    const prev = prevByArtist.get(artistKey(a));
    if (!prev) continue;
    const relabeledSlot = newByPosition.get(positionKey(prev));
    if (!relabeledSlot) continue;
    if (relabeledSlot.artist_name === a.artist_name) continue;
    const wasInSync =
      normalizeTime(prev.set_start) === normalizeTime(a.filming_start) &&
      normalizeTime(prev.set_end) === normalizeTime(a.filming_end);
    outcomes.push({
      id: a.id,
      artist_name: relabeledSlot.artist_name,
      filming_start: wasInSync ? relabeledSlot.set_start : a.filming_start,
      filming_end: wasInSync ? relabeledSlot.set_end : a.filming_end,
    });
  }

  return outcomes;
}

const toMinutes = (hhmm: string): number => {
  if (!hhmm || hhmm.length < 4) return 0;
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
};
